import React, { useEffect, useState } from 'react'
import { Barrister }                  from '../../entities/barrister'

export type BarristerOrder = 'seniority' | 'lastName'

const practiceAreasValues = {
    'criminalLaw': 'CRIMINAL_LAW',
    'publicLaw': 'PUBLIC_LAW',
    'tortLaw': 'TORT_LAW',
    'intellectualProperty': 'INTELLECTUAL_PROPERTY',
    'employmentIndustrial': 'EMPLOYMENT_INDUSTRIAL',
    'commercialLaw': 'COMMERCIAL_LAW'
}

type BarristerData = { edges: { node: Barrister }[] }
/***
 * Returns barristers in order of seniority, last-name.
 *
 * Seniority is silks first, followed by the year of coming to the bar, followed alphabetically by last name.
 *
 * In the case a barrister practices across multiple jurisdictions (internationally),
 * the earliest year of practice is identified and utilised
 * OR
 * if they have taken silk, the year silk was taken is used, and the year
 * of going to the bar in the jurisdiction where silk was taken is used.
 *
 * The earliest year the barrister went to the bar is used.
 *
 */
const useBarristersInOrder: (
    barristerData: BarristerData | undefined,
    orderBy: BarristerOrder,
    practiceAreaFilter: string | undefined
) => Barrister[] | undefined = (barristerData, orderBy, practiceAreaFilter) => {

    const [orderedBarristers, setOrderedBarristers] = useState<Barrister[] | undefined>()

    useEffect(() => {
        let barristers: Barrister[] = []
        if (barristerData) {

            barristerData.edges.forEach(({ node }) => {
                barristers.push({ ...node })
            })
            if (practiceAreaFilter) {
                barristers = filterBarristers(barristers, practiceAreaFilter) || []
            }
            if (barristers.length > 0) {


                switch (orderBy) {
                    case 'lastName':
                        setOrderedBarristers([...orderBarristersByLastName(barristers)])
                        break
                    case 'seniority':
                        setOrderedBarristers([...orderBarristersBySeniority(barristers)])
                        break
                }
            }
        }
    }, [orderBy, practiceAreaFilter])
    return orderedBarristers
}

export default useBarristersInOrder

function sortByLastName(barristers: Barrister[]): Barrister[] {
    return [...barristers.sort((a, b) => {
        const lastNameA = a.lastName.toLowerCase()
        const lastNameB = b.lastName.toLowerCase()
        return (lastNameA < lastNameB) ? -1 : (lastNameA > lastNameB) ? 1 : 0
    })]
}

function orderBarristersByLastName(unparsedBarristers: Barrister[]): Barrister[] {
    const { silks, barristers } = parseBarristers(unparsedBarristers)
    const sortedBarristers = sortByLastName(barristers)
    const sortedSilks = sortByLastName(silks)
    return [...sortedSilks, ...sortedBarristers]
}

/***
 * Sorts out which barristers have taken silk and returns
 * an object with silks and barristers separated out.
 *
 * @param unParsedBarristers list of barristers which may or may not contain silks
 */
function parseBarristers(unParsedBarristers: Barrister[]): { barristers: Barrister[], silks: Barrister[] } {
    const barristers: Barrister[] = []
    const silks: Barrister[] = []
    unParsedBarristers.forEach((barrister) => {
        if (barrister.practices &&
            barrister.practices[0] &&
            barrister.practices[0].takenSilk) {
            silks.push(barrister)
        } else {
            barristers.push(barrister)
        }
    })
    return {
        barristers,
        silks
    }
}

function orderBarristersBySeniority(unparsedBarristers: Barrister[]): Barrister[] {

    const { silks, barristers } = parseBarristers(unparsedBarristers)

    const barristersWithOrderMeta: Barrister[] = []
    const silkWithOrderMeta: Barrister[] = []


    // Add earliest year silk taken to meta
    silks.forEach((silk) => {

        let indexOfEarliestYearSilkTaken = 0
        let earliestYearSilkTaken = new Date().getFullYear()

        const _silk = { ...silk }

        _silk.practices.forEach((practice, idx) => {
            if (practice.takenSilk && practice.silkYear < earliestYearSilkTaken) {
                indexOfEarliestYearSilkTaken = idx
                earliestYearSilkTaken = practice.silkYear
            }
        })
        _silk.silkYear = earliestYearSilkTaken
        silkWithOrderMeta.push({ ..._silk })
    })

    // Order silks by `silkYear`
    const orderedSilks = silkWithOrderMeta.sort((a, b) => a.silkYear - b.silkYear)
    // Sort barristers by the earliest year they went to the bar across all their practices
    barristers.forEach((barrister) => {

        // If a barrister has only 1 practice:
        if (barrister.practices.length === 1) {
            barrister.joinedBarYear = barrister.practices[0].joinedBarYear
            barristersWithOrderMeta.push(barrister)
        }

        // If a barrister has no practice:
        else if (barrister.practices.length === 0) {
            barrister.joinedBarYear = -1
            barristersWithOrderMeta.push(barrister)
        }

        // If a barrister has more than one practice
        else {
            let indexOfEarliestYearJoinedBar = 0
            let earliestJoinedBarYear = new Date().getFullYear()
            barrister.practices.forEach((practice, idx) => {
                if (earliestJoinedBarYear > practice.joinedBarYear && practice.joinedBarYear !== null) {
                    indexOfEarliestYearJoinedBar = idx
                    earliestJoinedBarYear = practice.joinedBarYear
                }
            })
            barrister.joinedBarYear = barrister.practices[indexOfEarliestYearJoinedBar].joinedBarYear
            barristersWithOrderMeta.push(barrister)
        }
    })
    // Order barristers by joinedBarYear
    const orderedBarristers = [...barristersWithOrderMeta.sort((a, b) => a.joinedBarYear - b.joinedBarYear)]
    return [...orderedSilks, ...orderedBarristers]
}

function filterBarristers(barristers: Barrister[], practiceAreaId: string): Barrister[] | undefined {
    if (barristers) {
        return barristers.filter((barrister) => {
            return barrister.practiceAreas.some((pa) => {
                return pa.topLevelPracticeArea === practiceAreasValues[practiceAreaId]
            })
        })
    }
}

